import { FC, useState } from 'react'

import { useSuspenseQuery } from '@tanstack/react-query'

import {
  Button,
  Card,
  Cluster,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Icon,
  Stack,
  Typography,
} from '@forto-dev/phoenix-ui-core'

import { useLocalisation } from '@/utils/localisation'

import { CreateSubscriptionDialog } from './create'
import { subscriptionsQueryOptions } from '../api/subscriptions.service'
import { useSubscriptionDelete } from '../api/useSubscriptionDelete'
import { Subscription } from '../domain/subscription'

export const Subscriptions: FC = () => {
  const { translate } = useLocalisation()
  const deleteSubscriptionMutation = useSubscriptionDelete()
  const { data: subscriptions } = useSuspenseQuery(subscriptionsQueryOptions())
  const [showCreateDialog, setShowCreateDialog] = useState<boolean>(false)
  const [currentDeleteSubscription, setCurrentDeleteSubscription] = useState<Subscription | undefined>(undefined)

  return (
    <>
      <Stack gap={16}>
        <Stack>
          {subscriptions.map((subscription) => (
            <Card key={subscription.id}>
              <Cluster gap={16} x="between">
                <Stack gap={2}>
                  <Typography>{subscription.name}</Typography>
                  <Cluster gap={4} y="center">
                    <Icon name="Integration" color="muted" size="xs" />
                    <Typography color="muted" variant="small">
                      {subscription.url}
                    </Typography>
                  </Cluster>
                  <Cluster gap={4} y="center">
                    <Icon name="Info" color="muted" size="xs" />
                    <Typography color="muted" variant="small">
                      {subscription.eventTypes.join(', ')}
                    </Typography>
                  </Cluster>
                  <Cluster gap={4} y="center">
                    <Icon name="Lock" color="muted" size="xs" />
                    <Typography color="muted" variant="small">
                      {subscription.signatureKey}
                    </Typography>
                  </Cluster>
                </Stack>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon">
                      <Icon name="More" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent>
                    <DropdownMenuItem onClick={() => setCurrentDeleteSubscription(subscription)}>
                      {translate('integrations.subscriptions.list.actions.delete')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </Cluster>
            </Card>
          ))}
        </Stack>

        <Button onClick={() => setShowCreateDialog(true)}>
          {translate('integrations.subscriptions.actions.create')}
        </Button>
      </Stack>

      {currentDeleteSubscription ? (
        <Dialog defaultOpen>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{translate('integrations.subscriptions.delete.title')}</DialogTitle>
            </DialogHeader>
            <Typography>
              {translate('integrations.subscriptions.delete.message', {
                name: currentDeleteSubscription.name,
              })}
            </Typography>
            <DialogFooter>
              <Button variant="outline-primary" onClick={() => setCurrentDeleteSubscription(undefined)}>
                {translate('integrations.subscriptions.delete.actions.cancel')}
              </Button>
              <Button
                onClick={async () => {
                  await deleteSubscriptionMutation.mutateAsync({
                    subscriptionId: currentDeleteSubscription.id,
                  })
                  setCurrentDeleteSubscription(undefined)
                }}
              >
                {translate('integrations.subscriptions.delete.actions.delete')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      ) : null}

      {showCreateDialog ? <CreateSubscriptionDialog onClose={() => setShowCreateDialog(false)} /> : null}
    </>
  )
}
